<template>
  <div
    class="p-8 max-w-3xl flex-no-grow"
    :class="{ 'h-24 load load--fill': isLoading }"
  >
    <div
      v-if="!queue.rows.length && !isLoading"
      class="empty"
    >
      <section v-if="completed">
        <h2 v-text="'Import Complete!'" />
        <a
          :href="`/members/imports/${memberCSVUpload.uuid}/logs`"
          v-text="'View logs'"
        />
      </section>
      <section v-else>
        <h2 v-text="'Processing the data...'" />
        <p v-text="'Try refreshing this page or come back in a few minutes.'" />
      </section>
    </div>
    <div v-if="queue.rows.length && !isLoading">
      <h1>
        Processing {{ queue.rows.length }} of {{ queue.meta.pagination.total }} Pending Members for {{
          memberCSVUpload.attributes.original_filename
        }}
      </h1>
      <div class="flex items-center">
        <div class="flex mb-8 mr-8 ml-1 items-center">
          <div class="mr-2">
            <input
              id="importAllAs"
              type="checkbox"
              name="importAllAs"
              :checked="importAllAs"
              @change="toggleImportAllAs"
            >
            <label
              for="importAllAs"
              class="uppercase mb-0"
              v-text="'Import all as:'"
            />
          </div>
          <div>
            <div class="select-container h-auto">
              <select
                id="selectedRole"
                v-model="selectedRole"
                name="selectedRole"
                class="mb-0 h-auto"
                @change="updateSelectedRole"
              >
                <option
                  v-for="role in availableRoles"
                  :key="role.uuid"
                  :value="role.attributes.name"
                  v-text="role.attributes.title"
                />
              </select>
            </div>
          </div>
        </div>
        <div class="flex flex-1 items-center mb-8">
          <div class="mr-2 pt-1">
            <input
              id="addAllMembersTo"
              type="checkbox"
              name="addAllMembersTo"
              :checked="addAllTo"
              @change="toggleAddAllTo"
            >
            <label
              for="addAllMembersTo"
              class="uppercase"
              v-text="'Add all to:'"
            />
          </div>
          <div class="flex-grow">
            <Multiselect
              id="titles"
              v-model="selectedGroups"
              :options="availableGroups"
              :loading="isLoadingGroups"
              open-direction="below"
              label="label"
              track-by="uuid"
              placeholder="Group(s)"
              :multiple="true"
              :clear-on-select="false"
              :close-on-select="false"
              @search-change="handleSearchChange"
            />
          </div>
        </div>
      </div>
      <div
        class="table-responsive mt-0"
        style="overflow-x: visible"
      >
        <table class="table table-clean min-w-table">
          <thead>
            <tr>
              <th>
                <input
                  id="inviteAll"
                  type="checkbox"
                  name="inviteAll"
                  :checked="allAreInvited"
                  @change="toggleInviteAll"
                >
                <label
                  for="inviteAll"
                  class="mb-0"
                  v-text="'Invite'"
                />
              </th>
              <th v-text="'Role'" />
              <th v-text="'Group(s)'" />
              <th
                v-for="(value, key) in tableHeadings"
                :key="key"
                v-text="key"
              />
            </tr>
          </thead>
          <tbody v-if="queue.rows.length">
            <tr
              is="ImportDetailsRow"
              v-for="row in queue.rows"
              :key="row.uuid"
              :row="row"
              :available-roles="availableRoles"
              :all-are-invited="allAreInvited"
              :available-groups="availableGroups"
            />
          </tbody>
          <tbody v-else>
            <tr>
              <td :colspan="tableHeadings.length">
                <h3 v-text="'There are no actionable rows'" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-around">
        <button
          v-if="queue.rows.length"
          type="button"
          class="btn my-8"
          @click="save"
          v-text="confirmText"
        />
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import { debounce } from 'lodash'
import Multiselect from 'vue-multiselect'
import ImportDetailsRow from './ImportDetailsRow'

export default {
  name: 'ImportDetails',
  components: {
    Multiselect,
    ImportDetailsRow,
  },
  props: {
    memberCSVUploadUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingGroups: false,
      completed: false,
      selectedRole: 'union-member',
      membershipGroups: [],
      selectedGroups: [],
      memberCSVUpload: {
        uuid: null,
        attributes: {
          original_filename: null,
        },
        availableRoles: {
          data: [],
        },
      },
    }
  },
  computed: {
    confirmText() {
      return `Assign Roles and Invite Members (${this.queue.rows.length})`
    },
    queue() {
      return this.$store.state.membership.memberCSVUploadApprovalQueue
    },
    allAreInvited() {
      return _.findIndex(this.queue.rows, { invite: false }) === -1
    },
    importAllAs() {
      return _.findIndex(this.queue.rows, row => row.role !== this.selectedRole) === -1
    },
    addAllTo() {
      return _.findIndex(this.queue.rows, row => row.groups !== this.selectedGroups) === -1
    },
    availableRoles() {
      return _.filter(this.memberCSVUpload.availableRoles.data, role => !['base-permissions', 'empowered-user'].includes(role.attributes.name))
    },
    availableGroups(){
      return this.membershipGroups.map(option => ({
        label: option.attributes.title,
        uuid: option.uuid,
      }))
    },
    tableHeadings() {
      return this.queue.rows.length
        ? this.queue.rows[0].attributes.contents
        : []
    },
  },
  watch: {
    importAllAs(newVal) {
      if (newVal) {
        this.updateSelectedRole()
      }
    },
    addAllTo(newVal) {
      if(newVal) {
        this.updateSelectedGroups()
      }
    },
    selectedGroups() {
      this.updateSelectedGroups()
    },
  },
  async mounted() {
    await this.$nextTick()

    this.fetch()
  },
  methods: {
    fetch() {
      this.isLoading = true

      this.$api.active().get(`memberships/imports/${this.memberCSVUploadUuid}?include=availableRoles`)
        .then((response) => {
          this.memberCSVUpload.uuid = response.data.uuid
          this.memberCSVUpload.attributes.original_filename = response.data.attributes.original_filename
          this.memberCSVUpload.availableRoles = response.data.availableRoles

          this.getMembershipGroups().then((response) => {
            this.membershipGroups = response.data.data
          })

          this.getMemberCSVs()
        })
    },
    handleSearchChange(search) {
      this.isLoadingGroups = true

      this.updateMembershipGroups(search)
    },
    updateMembershipGroups: debounce(function (search) {
      this.getMembershipGroups({
        search: search,
      }).then((response) => {
        this.membershipGroups = response.data.data
        this.isLoadingGroups = false
      })
    }, 500),
    getMembershipGroups(data = {}) {
      let uri = 'memberships/membership-groups'

      if (data.hasOwnProperty('search')) {
        uri = `${uri}?query=${data.search}`
      }

      return this.$api.active().get(uri)
    },
    getMemberCSVs(page = 1) {
      this.$api.active().get(`memberships/imports/${this.memberCSVUploadUuid}/member-csvs?status=readyForProcessing&perPage=50&page=${page}`)
        .then(({ data }) => {
          this.$store.commit('membershipSetCSVRowsOnApprovalQueue', data)

          this.isLoading = false

          if (data.meta.pagination.total === 0) {
            this.completed = true
          }
        })
    },
    save() {
      this.isLoading = true

      this.$store.commit('baseClearNotifications')

      this.$api.active().post(`memberships/imports/${this.memberCSVUploadUuid}/member-csvs/process-imported`, {
        memberCSVs: this.queue.rows.map(row => ({
          uuid: row.uuid,
          invite: row.invite,
          groups: row.groups.map(group => group.uuid),
          role: row.role,
        })),
      }).then(() => {
        this.$store.commit('membershipResetMemberCSVApprovalQueue')

        this.getMemberCSVs()
      }).catch((errors) => {
        this.isLoading = false

        this.$store.commit('baseAddNotification', {
          title: 'Error!',
          text: errors,
          type: 'error',
          timeout: false,
        })
      })
    },
    toggleInviteAll() {
      this.$store.commit('membershipToggleInviteOnApprovalQueue', !this.allAreInvited)
    },
    toggleImportAllAs() {
      this.$store.commit('membershipSetImportAllAsOnApprovalQueue', this.selectedRole)
    },
    updateSelectedRole() {
      this.$store.commit('membershipSetImportAllAsOnApprovalQueue', this.selectedRole)
    },
    toggleAddAllTo() {
      this.$store.commit('membershipSetAddAllToOnApprovalQueue', this.selectedGroups)
    },
    updateSelectedGroups() {
      this.$store.commit('membershipSetAddAllToOnApprovalQueue', this.selectedGroups)
    },
  },
}
</script>
